import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchProducts,
  bulkUpdateCart,
  fetchCart,
  addToCart as addToCartAPI,
  incrementCartQty as incrementCartQtyAPI,
  decrementCartQty as decrementCartQtyAPI,
  clearCart as clearCartAPI
} from "./productsAPI";
import { unwrapApiResponse } from "../../util/util";

// Only keep non-cart related persisted state
const persistedState = JSON.parse(localStorage.getItem("persist:root")) || [];
const persistedProducts = persistedState.products
  ? JSON.parse(persistedState.products)
  : {};
const persistedCacheKey = persistedProducts.cacheKey;

const initialState = {
  allProducts: [],
  allOptions: [],
  status: "idle",
  basket: [], // Always from API
  isFavorite: false,
  isEnterOfferCode: false,
  offerMessage: "",
  openHalfCart: false,
  searchKey: "",
  cacheKey: persistedCacheKey,
  cartId: null, // Always from API
  getCartStatus: "idle",
  cartData: {},
  getCartError: "",
  // Cart operation statuses
  addToCartStatus: "idle",
  updateCartStatus: "idle",
  clearCartStatus: "idle"
};

export const getProducts = createAsyncThunk(
  "products/fetchProducts",
  async (
    { categoryId, selectedLocation, page = 1, limit = 10 },
    { rejectWithValue }
  ) => {
    const locationId = selectedLocation?.id ? selectedLocation.id : "";
    try {
      // Key for cache
      const cacheKey = `products_${categoryId}_${page}_${limit}`;
      const persistedState =
        JSON.parse(localStorage.getItem("persist:root")) || {};
      const persistedProducts = persistedState.products
        ? JSON.parse(persistedState.products)
        : {};
      const cached = persistedProducts[cacheKey];

      // Check cache validity (30 seconds)
      if (
        cached &&
        cached.data &&
        cached.timestamp &&
        Date.now() - cached.timestamp < 5 * 1000
      ) {
        return cached.data;
      }
      // Fetch from API
      const response = await fetchProducts({
        page,
        limit,
        categoryId,
        locationId
      });
      const data = unwrapApiResponse(response, rejectWithValue);

      // Save to cache
      persistedProducts[cacheKey] = {
        data,
        timestamp: Date.now()
      };
      persistedState.products = JSON.stringify(persistedProducts);
      localStorage.setItem("persist:root", JSON.stringify(persistedState));

      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch products");
    }
  },
  {
    condition: ({ categoryId, page = 1, limit = 10 }, { getState }) => {
      const state = getState();
      return state.products.status !== "loading";
    }
  }
);

export const bulkAddToCart = createAsyncThunk(
  "products/bulkAddToCart",
  async (items, { rejectWithValue }) => {
    try {
      const response = await bulkUpdateCart(items);
      return unwrapApiResponse(response, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to bulk add to cart");
    }
  }
);

export const getCart = createAsyncThunk(
  "cart/getCart",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchCart();
      return unwrapApiResponse(response, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch cart");
    }
  }
);

export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async ({ productVariantId, quantity = 1 }, { rejectWithValue, dispatch }) => {
    try {
      const response = await addToCartAPI({ productVariantId, quantity });
      const result = unwrapApiResponse(response, rejectWithValue);

      // Automatically refresh cart after successful add
      dispatch(getCart());

      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to add to cart");
    }
  }
);

export const increaseCartQty = createAsyncThunk(
  "cart/incrementQty",
  async (
    { bundleId, productVariantId, incrementBy = 1 },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await incrementCartQtyAPI({
        bundleId,
        productVariantId,
        incrementBy
      });
      const result = unwrapApiResponse(response, rejectWithValue);

      // Automatically refresh cart after successful update
      dispatch(getCart());

      return result;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to increment cart quantity"
      );
    }
  }
);

export const decreaseCartQty = createAsyncThunk(
  "cart/decrementQty",
  async (
    { bundleId, productVariantId, decrementBy = 1 },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await decrementCartQtyAPI({
        bundleId,
        productVariantId,
        decrementBy
      });
      const result = unwrapApiResponse(response, rejectWithValue);

      // Automatically refresh cart after successful update
      dispatch(getCart());

      return result;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to decrement cart quantity"
      );
    }
  }
);

export const clearCart = createAsyncThunk(
  "cart/clearCart",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await clearCartAPI();
      const result = unwrapApiResponse(response, rejectWithValue);

      // Clear local cart state immediately
      dispatch(clearCartLocal());

      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to clear cart");
    }
  }
);

export const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    // Only keep essential UI state reducers
    showHalfCart: (state, action) => {
      state.openHalfCart = action.payload;
    },
    setSearchKey: (state, action) => {
      state.searchKey = action.payload;
    },
    // Internal reducer for immediate cart clearing
    clearCartLocal: (state) => {
      state.basket = [];
      state.cartId = null;
      state.cartData = {};
    }
  },
  extraReducers: (builder) => {
    builder
      // Products
      .addCase(getProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.status = "idle";
        state.allProducts = action.payload;
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.status = "failed";
      })

      // Bulk add to cart
      .addCase(bulkAddToCart.pending, (state) => {
        state.addToCartStatus = "loading";
      })
      .addCase(bulkAddToCart.fulfilled, (state) => {
        state.addToCartStatus = "idle";
      })
      .addCase(bulkAddToCart.rejected, (state) => {
        state.addToCartStatus = "failed";
      })

      // Get cart
      .addCase(getCart.pending, (state) => {
        state.getCartStatus = "loading";
      })
      .addCase(getCart.fulfilled, (state, action) => {
        state.getCartStatus = "idle";
        state.cartData = action.payload;
        state.getCartError = "";

        // Update basket and cartId from API response
        /* if (action.payload) {
          state.basket = action.payload.items || action.payload.products || [];
          state.cartId = action.payload.id || action.payload.cartId;
        } */
      })
      .addCase(getCart.rejected, (state, action) => {
        state.getCartStatus = "failed";
        state.getCartError = action.payload || action.error.message;
      })

      // Add to cart
      .addCase(addToCart.pending, (state) => {
        state.addToCartStatus = "loading";
      })
      .addCase(addToCart.fulfilled, (state) => {
        state.addToCartStatus = "idle";
      })
      .addCase(addToCart.rejected, (state) => {
        state.addToCartStatus = "failed";
      })

      // Update cart quantity (both increment and decrement)
      .addCase(increaseCartQty.pending, (state) => {
        state.updateCartStatus = "loading";
      })
      .addCase(increaseCartQty.fulfilled, (state) => {
        state.updateCartStatus = "idle";
      })
      .addCase(increaseCartQty.rejected, (state) => {
        state.updateCartStatus = "failed";
      })

      .addCase(decreaseCartQty.pending, (state) => {
        state.updateCartStatus = "loading";
      })
      .addCase(decreaseCartQty.fulfilled, (state) => {
        state.updateCartStatus = "idle";
      })
      .addCase(decreaseCartQty.rejected, (state) => {
        state.updateCartStatus = "failed";
      })

      // Clear cart
      .addCase(clearCart.pending, (state) => {
        state.clearCartStatus = "loading";
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.clearCartStatus = "idle";
      })
      .addCase(clearCart.rejected, (state) => {
        state.clearCartStatus = "failed";
      });
  }
});

export const { showHalfCart, setSearchKey, clearCartLocal } =
  productSlice.actions;

// Selectors
export const allProducts = (state) => state.products.allProducts;
export const productsStatus = (state) => state.products.status;
export const cart = (state) => state.products.basket;
export const cartCount = (state) =>
  state.products.basket.reduce(
    (total, item) => total + (item.quantity || item.count || 0),
    0
  );
export const cartTotal = (state) =>
  state.products.basket.reduce(
    (total, item) =>
      total + (item.price || 0) * (item.quantity || item.count || 0),
    0
  );
export const isFavorite = (state) => state.products.isFavorite;
export const isEnterOfferCode = (state) => state.products.isEnterOfferCode;
export const offerMessage = (state) => state.products.offerMessage;
export const allOptions = (state) => state.products.allOptions;
export const openHalfCart = (state) => state.products.openHalfCart;
export const searchKey = (state) => state.products.searchKey;
export const cartId = (state) => state.products.cartId;
export const getCartStatus = (state) => state.products.getCartStatus;
export const cartData = (state) => state.products.cartData;
export const getCartError = (state) => state.products.getCartError;
export const addToCartStatus = (state) => state.products.addToCartStatus;
export const updateCartStatus = (state) => state.products.updateCartStatus;
export const clearCartStatus = (state) => state.products.clearCartStatus;

// Combined loading selector for better UX
export const isCartLoading = (state) => {
  return (
    state.products.getCartStatus === "loading" ||
    state.products.addToCartStatus === "loading" ||
    state.products.updateCartStatus === "loading" ||
    state.products.clearCartStatus === "loading"
  );
};

export default productSlice.reducer;
